/**
 * AdminAgentSync — Sprint AgentSync SC-008
 *
 * Admin UI for manual agent sync trigger and sync status display.
 * Calls POST /admin/agents/sync (admin JWT required).
 */

import { useState } from 'react';
import { RefreshCw, Users, CheckCircle2, AlertCircle, Clock, Image } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import apiClient from '@/services/api-client';
import { logger } from '../services/logger.service';

interface SyncResult {
  agentsSynced: number;
  agentsCreated: number;
  agentsUpdated: number;
  photosMirrored: number;
  listingsUpdated: number;
  newAgentsForCognito: string[];
  message?: string;
}

export function AdminAgentSync() {
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastResult, setLastResult] = useState<SyncResult | null>(null);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);

  const triggerSync = async (fullSync = false) => {
    setIsSyncing(true);
    try {
      const result = await apiClient.post<SyncResult>('/admin/agents/sync', { fullSync });
      setLastResult(result);
      setLastSyncTime(new Date());
      if (result.message === 'Sync already in progress') {
        toast.info('Sync already running — try again in a moment');
      } else {
        toast.success(`Sync complete — ${result.agentsSynced} agents processed`);
      }
    } catch (err: any) {
      logger.error('Agent sync failed', err as Error, undefined, 'AdminAgentSync');
      toast.error(`Sync failed: ${err?.message || 'Unknown error'}`);
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Agent Sync</h2>
        <p className="text-muted-foreground text-sm mt-1">
          Sync agent profiles from Property24, mirror photos to S3, and provision CRM access.
        </p>
      </div>

      {/* Sync Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Manual Sync Trigger</CardTitle>
          <CardDescription>
            Incremental sync fetches changes from the last 24 hours. Full sync fetches all agents.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button onClick={() => triggerSync(false)} disabled={isSyncing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
            {isSyncing ? 'Syncing...' : 'Incremental Sync'}
          </Button>
          <Button variant="outline" onClick={() => triggerSync(true)} disabled={isSyncing}>
            <Users className="w-4 h-4 mr-2" />
            Full Sync
          </Button>
        </CardContent>
      </Card>

      {/* Last Sync Result */}
      {lastResult && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                Last Sync Result
              </CardTitle>
              {lastSyncTime && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {lastSyncTime.toLocaleTimeString()}
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {lastResult.message === 'Sync already in progress' ? (
              <div className="flex items-center gap-2 text-amber-500">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">Sync already in progress — another process is running.</span>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <StatCard label="Agents Synced" value={lastResult.agentsSynced} icon={<Users className="w-4 h-4" />} />
                <StatCard label="New Agents" value={lastResult.agentsCreated} icon={<CheckCircle2 className="w-4 h-4 text-green-500" />} />
                <StatCard label="Updated" value={lastResult.agentsUpdated} icon={<RefreshCw className="w-4 h-4" />} />
                <StatCard label="Photos Mirrored" value={lastResult.photosMirrored} icon={<Image className="w-4 h-4" />} />
                <StatCard label="Listings Updated" value={lastResult.listingsUpdated} icon={<Users className="w-4 h-4" />} />
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground">Cognito Queued</p>
                  <p className="text-xl font-bold mt-1">{lastResult.newAgentsForCognito?.length || 0}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Info card */}
      <Card className="border-dashed">
        <CardContent className="pt-6">
          <div className="text-sm text-muted-foreground space-y-1">
            <p>• Incremental sync runs automatically every night at 2:00 AM SAST</p>
            <p>• Agent photos are mirrored from Property24 to S3 during sync</p>
            <p>• New agents are automatically provisioned with Cognito CRM access</p>
            <p>• Agent contact details are written to all their listing records</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ label, value, icon }: { label: string; value: number; icon: React.ReactNode }) {
  return (
    <div className="p-3 rounded-lg bg-muted/50">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
        {icon}
        <span>{label}</span>
      </div>
      <p className="text-xl font-bold">{value}</p>
    </div>
  );
}
